import { Topic } from '@/types'
import { PatternNoteTypes, TopicType } from '@/utils/constants'
import { getGalleryPrintMeshes } from '@/features/gallery-wizard/galleryMeshCatalog'
import { StudioArt, StudioSceneState } from '../types'
import { ROOM_DEFAULTS, DEFAULT_ART_DEFAULTS } from '../constants'
import { getClassicVariant, DEFAULT_CLASSIC_FRAME_ID, DEFAULT_CLASSIC_MAT_W } from '../classicCatalog'
import { generateLocalId } from '../babylon/sceneMath'

interface LegacyPrintEntry {
  url?: string
  id?: string
}

type LegacyColorNote = Record<string, Array<{ r: number; g: number; b: number }>>

function findNoteText(topic: Topic, noteName: keyof typeof PatternNoteTypes): string | undefined {
  const noteType = PatternNoteTypes[noteName]
  return topic.notes.find((n) => n.noteName === noteName && n.noteType === noteType)?.noteText
}

function parseJSON<T>(text: string | undefined): T | undefined {
  if (!text) return undefined
  try {
    return JSON.parse(text) as T
  } catch {
    return undefined
  }
}

/** `readAndApplyColor` in src/pages/pattern/renderer/main.js converts each {r,g,b} the same way — 0..1 floats, not 0..255. */
function rgbToHex({ r, g, b }: { r: number; g: number; b: number }): string {
  const channel = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0')
  const to255 = (v: number) => (v <= 1 ? v * 255 : v)
  return `#${channel(to255(r))}${channel(to255(g))}${channel(to255(b))}`
}

/**
 * Reconstructs a `StudioSceneState` for a legacy "Classic" gallery — one of
 * the 14 hand-authored `.babylon` diorama shapes, built and saved entirely
 * through the OLD pattern editor (`src/pages/pattern/`), which has never
 * heard of this Studio's own `studio3dSceneStateV1` note. Without this,
 * `useStudio3DData`'s seed falls back to `ROOM_DEFAULTS` — a *procedural*
 * room — for any such gallery's first-ever visit here, silently discarding
 * its real room shape, photos, and frame choice (see git history/PR notes
 * around 2026-07-23 "the entire scenes are not right" for the bug report
 * that prompted this).
 *
 * Reads the same flat `PatternNoteTypes` notes the legacy renderer itself
 * reads (`src/pages/pattern/renderer/main.js`) — `templateID` picks the
 * room shape/mesh, `print` textures each pre-baked slot mesh (keyed by its
 * on-disk `template_Shape_NNN` name — see `getGalleryPrintMeshes`), `frameID`
 * is one frame choice for the whole topic (`getFrame`/`applyGalleryFrame` in
 * main.js — mirrored here as `classicFrameId` on every art, same as
 * `useStudioLegacyRoom`'s own per-print default), and `matting`/`color` are
 * the border width and optional per-slot tint (`readAndApplyColor` in
 * main.js — same 0..1 float RGB it converts to hex).
 *
 * Only meaningful for GALLERY topics (`topicType === '201'`) whose
 * `templateID` is a real `GalleryTemplates` id — anything else (a legacy 2D
 * "pattern" layout, or a gallery that was only ever a plain wall of
 * freely-placed prints) returns `null` so the normal seed fallback
 * (existing studio3d note > real album photos > placeholder demo) applies.
 *
 * Slot alignment is positional (art index → `getGalleryPrintMeshes` index),
 * matching `useStudioLegacyRoom`'s own assumption — a legacy topic whose
 * populated slots have *interior* gaps (e.g. only slot 3 of 6 has a photo)
 * compacts to a dense prefix instead of preserving the exact gap, since
 * `useStudioLegacyRoom` has no slot-name-aware matching to place it
 * correctly either way. In practice galleries are filled slot 1 upward, so
 * this only mis-renders the rarer case of a photo removed from the middle
 * of an otherwise-full room.
 */
export function convertLegacyClassicGallery(topic: Topic | null): StudioSceneState | null {
  if (!topic || String(topic.topicType) !== String(TopicType.GALLERY)) return null

  const templateIdText = findNoteText(topic, 'templateID')
  const templateId = templateIdText ? Number(templateIdText) : NaN
  if (!Number.isFinite(templateId) || !getClassicVariant(templateId)) return null

  const prints = parseJSON<Record<string, LegacyPrintEntry>>(findNoteText(topic, 'print')) || {}
  const colors = parseJSON<LegacyColorNote>(findNoteText(topic, 'color')) || {}
  // Legacy's `matting.width` (RH.CreateMatting in renderHelpers.js) is a
  // world-space *meters* inset, while `classicMatW` here is a *fraction* of
  // each slot's own size (slots vary widely in physical size across the 14
  // room shapes — see its doc comment in types.ts). Converting meters to a
  // fraction needs that slot's real bounding box, which isn't available
  // from note text alone (no scene loaded yet at conversion time) — so
  // this intentionally ignores the legacy value rather than passing a
  // meters number through as if it were already a fraction.
  const matW = DEFAULT_CLASSIC_MAT_W

  const frameIdText = findNoteText(topic, 'frameID')
  const frameId = frameIdText && frameIdText !== 'null' ? Number(frameIdText) : undefined
  const classicFrameId = Number.isFinite(frameId) ? frameId : DEFAULT_CLASSIC_FRAME_ID

  const slotNames = getGalleryPrintMeshes(templateId)
  const arts: StudioArt[] = []
  slotNames.forEach((catalogName, i) => {
    const entry = prints[catalogName]
    if (!entry?.url) return

    const rgbEntry = colors[catalogName]?.[0]
    const matTone = rgbEntry ? rgbToHex(rgbEntry) : DEFAULT_ART_DEFAULTS.matTone

    arts.push({
      id: generateLocalId('art'),
      egoId: entry.id?.replace(/^egoID-/, '') || undefined,
      img: entry.url,
      name: `Print ${i + 1}`,
      y: 2.3,
      u: 0,
      side: 0,
      ratio: DEFAULT_ART_DEFAULTS.ratio,
      w: DEFAULT_ART_DEFAULTS.w,
      profile: DEFAULT_ART_DEFAULTS.profile,
      finish: DEFAULT_ART_DEFAULTS.finish,
      matW: DEFAULT_ART_DEFAULTS.matW,
      matTone,
      spotI: DEFAULT_ART_DEFAULTS.spotI,
      warm: DEFAULT_ART_DEFAULTS.warm,
      classicFrameId,
      classicMatW: matW,
    })
  })
  if (arts.length === 0) return null

  return {
    version: 1,
    room: { ...ROOM_DEFAULTS, mode: 'classic', classicTemplateId: templateId },
    arts,
    pins: [],
  }
}
